import dayjs from "dayjs";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IDialysisSessionTableRow } from "../../../../../../@types/Measurements";
import { createColumn, Table } from "../../../../../../components/Table";
import { useDialysisSessionTable } from "../../../../../../hooks/queries/measurements";
import { LONG_DATE_TIME_FORMAT } from "../../../../../../utils/dateFormatter";
import {
  ImageContainer,
  NoContentMessage,
} from "../ModalMeasurementsTable/ModalMeasurementsTable.styles";
import { DialysisTableContainer } from "./ModalDialysisTable.styles";

interface IModalDialysisTableProps {
  patientId: string | undefined;
  fromDate?: string;
  toDate?: string;
  isPinnedGraph?: boolean;
}

const formatBp = (
  systolic: number | null,
  diastolic: number | null
): string => {
  if (systolic == null && diastolic == null) return "-";
  return `${systolic ?? "-"}/${diastolic ?? "-"}`;
};

const formatValue = (value: number | null): string => {
  if (value == null) return "-";
  return value.toString();
};

const columns = [
  createColumn({
    keyName: "preBp",
    header: () => i18next.t("patientPage_columns_pre_dialysis_bp_mmhg"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "postBp",
    header: () => i18next.t("patientPage_columns_post_dialysis_bp_mmhg"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "targetWeight",
    header: () => i18next.t("patientPage_columns_target_weight_dry_kg"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "ufVolume",
    header: () => i18next.t("patientPage_columns_uf_volume_l"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "preWeight",
    header: () => i18next.t("patientPage_columns_pre_dialysis_weight_kg"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "postWeight",
    header: () => i18next.t("patientPage_columns_post_dialysis_weight_kg"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "idwg",
    header: () => i18next.t("patientPage_columns_idwg_kg"),
    cell: (info: any) => info.renderValue(),
  }),
  createColumn({
    keyName: "date",
    header: () => i18next.t("patientPage_columns_date"),
    cell: (info: any) => info.renderValue(),
    size: 180,
  }),
];

export function ModalDialysisTable({
  patientId,
  fromDate,
  toDate,
  isPinnedGraph,
}: IModalDialysisTableProps) {
  const { t } = useTranslation();

  const { data, isFetching } = useDialysisSessionTable({
    patientId,
    fromDate,
    toDate,
  });

  const tableData = useMemo(() => {
    if (!data) return [];
    return data.map((session: IDialysisSessionTableRow) => ({
      preBp: formatBp(session.pre_bp_systolic, session.pre_bp_diastolic),
      postBp: formatBp(session.post_bp_systolic, session.post_bp_diastolic),
      targetWeight: formatValue(session.target_weight),
      ufVolume: formatValue(session.uf_volume),
      preWeight: formatValue(session.pre_weight),
      postWeight: formatValue(session.post_weight),
      idwg: formatValue(session.idwg),
      date: dayjs(session.date).format(LONG_DATE_TIME_FORMAT),
    }));
  }, [data]);

  if (isFetching) {
    return (
      <DialysisTableContainer isPinnedGraph={isPinnedGraph}>
        <ImageContainer>
          <NoContentMessage>
            {t("patientPage_measurementModal_loading")}
          </NoContentMessage>
        </ImageContainer>
      </DialysisTableContainer>
    );
  }

  if (tableData.length === 0) {
    return (
      <DialysisTableContainer isPinnedGraph={isPinnedGraph}>
        <ImageContainer>
          <NoContentMessage>
            {t("patientPage_measurementModal_noDialysis")}
          </NoContentMessage>
        </ImageContainer>
      </DialysisTableContainer>
    );
  }

  return (
    <DialysisTableContainer isPinnedGraph={isPinnedGraph}>
      <Table columns={columns} data={tableData} hasBorder={false} />
    </DialysisTableContainer>
  );
}
